import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { Cart, format, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { humanizeUnitOfMeasure } from '../unit-of-measure';
import { Tooltip } from '@msdyn365-commerce-modules/utilities';
import { getInitialBreakoffState } from '../common/product-list-order-quantity';

export type CartCountProps = {
    context: ICoreContext;
    product: SimpleProduct | undefined;
    cart: Cart | undefined;
    inCartTooltip?: string;
    nonBreakoffUOM?: string;
};

const CartCount: React.FC<CartCountProps> = ({
    context,
    product,
    cart,
    inCartTooltip,
    nonBreakoffUOM
}) => {
    const [showTooltip, setShowTooltip] = React.useState(false);

    if (!product) {
        return null;
    }

    const cartQuantity = cart?.CartLines
        ?.filter(x => x.ProductId === product.RecordId)
        .reduce((acc, val) => acc + (val.Quantity || 0), 0);

    if (!cartQuantity) {
        return null;
    }

    const baseClassName = 'cart-count';
    const cartCountId = `product-cart-count-${product.RecordId}`;
    const tooltipString = inCartTooltip || '{0} {1} i handlekurven';

    const breakoffState = getInitialBreakoffState(cartQuantity, product);
    const multiplier = product.Behavior?.MultipleOfQuantity || 1;
    const displayQuantity = breakoffState.breakoff ? cartQuantity : cartQuantity / multiplier;

    // Check if there is a friendly UOM defined on the SimpleProduct
    const friendlyUOM = product?.ExtensionProperties?.find((x) => x.Key === 'FRIENDLYUOM')?.Value?.StringValue;
    const humanizedUOM = breakoffState.breakoff || !nonBreakoffUOM
        ? humanizeUnitOfMeasure(context, friendlyUOM || product.DefaultUnitOfMeasure, product)
        : humanizeUnitOfMeasure(context, nonBreakoffUOM);

    const tooltipText = format(tooltipString, displayQuantity, humanizedUOM);

    return (
        <div
            id={cartCountId}
            className={baseClassName}
            onMouseOver={() => setShowTooltip(true)}
            onMouseOut={() => setShowTooltip(false)}
            onTouchStart={() => setShowTooltip(true)}
        >
            <span className={`${baseClassName}__label__quantity`}>{displayQuantity}</span>
            <span className={`${baseClassName}__label__uom`}>{humanizedUOM}</span>

            <Tooltip
                isOpen={showTooltip}
                target={`#${cartCountId}`}
                className='msc-cart-count-tooltip'
            >
                {tooltipText}
            </Tooltip>
        </div>
    );
};

export default CartCount;
